import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, PACK_SIZES } from "@/lib/pharmacy";
import { toast } from "sonner";
import { Plus, Trash2, Edit, X, Upload, Loader2, PackageOpen } from "lucide-react";

const empty = {
  name: "", generic_name: "", brand: "", strength: "", manufacturer: "", category: "",
  description: "", price_per_pack: {}, discount_percent: 0, image_url: "",
  sale_price_per_pill: 0, original_price_per_pill: 0, original_packaging: "", discount_percentage: 0,
  prescription_required: false, in_stock: true, featured: false,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asAbsolute = (u) => u ? (u.startsWith("/") ? `${BACKEND}${u}` : u) : "";

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [rowUploading, setRowUploading] = useState(null);
  const fileRef = useRef(null);
  const rowFileRef = useRef(null);
  const zipRef = useRef(null);
  const [rowTarget, setRowTarget] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);

  const load = () => api.get("/products", { params: q ? { q } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [q]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const open = (p) => {
    setEditing(p);
    setForm(p ? { ...empty, ...p, price_per_pack: p.price_per_pack || {} } : empty);
  };
  const close = () => { setEditing(null); setForm(empty); };
  const setPrice = (size, val) => setForm({ ...form, price_per_pack: { ...form.price_per_pack, [size]: val ? Number(val) : 0 } });

  const uploadImage = async (file, onDone) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/product-image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    onDone(data.url);
  };

  const handleFormFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      await uploadImage(file, (url) => { setForm((f) => ({ ...f, image_url: url })); toast.success("Image uploaded"); });
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleRowFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !rowTarget) return;
    setRowUploading(rowTarget.id);
    try {
      await uploadImage(file, async (url) => {
        await api.put(`/products/${rowTarget.id}`, { ...rowTarget, image_url: url });
        toast.success(`Image saved for ${rowTarget.name}`);
        load();
      });
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setRowUploading(null); setRowTarget(null); e.target.value = ""; }
  };

  const save = async () => {
    try {
      const cleanPrices = Object.fromEntries(Object.entries(form.price_per_pack || {}).filter(([,v]) => Number(v) > 0));
      const payload = {
        ...form, price_per_pack: cleanPrices,
        discount_percent: Number(form.discount_percent || 0),
        sale_price_per_pill: Number(form.sale_price_per_pill || 0),
        original_price_per_pill: Number(form.original_price_per_pill || 0),
        discount_percentage: Number(form.discount_percentage || 0),
      };
      if (editing?.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); load(); };

  const handleZip = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Please pick a .zip file"); e.target.value=""; return; }
    setBulkBusy(true); setBulkReport(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/bulk-image-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkReport(data);
      toast.success(`${data.matched_count} product image(s) updated`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBulkBusy(false); e.target.value = ""; }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Products</h1>
        <div className="flex gap-2">
          <input ref={zipRef} type="file" accept=".zip" className="hidden" onChange={handleZip}/>
          <button onClick={() => zipRef.current?.click()} disabled={bulkBusy} className="btn-ghost" data-testid="admin-bulk-import">
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : <PackageOpen className="h-4 w-4"/>}
            {bulkBusy ? "Importing..." : "Bulk image import (.zip)"}
          </button>
          <button onClick={() => open(null)} className="btn-primary" data-testid="admin-add-product"><Plus className="h-4 w-4"/> Add product</button>
        </div>
      </div>

      {bulkReport && (
        <div className="card-flat p-4 mb-4" data-testid="bulk-report">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-emerald-700 font-semibold">✓ {bulkReport.matched_count} matched</span>
            <span className="text-amber-700">↳ {bulkReport.unmatched_count} unmatched</span>
            <span className="text-slate-500">— {bulkReport.skipped_count} skipped</span>
            {bulkReport.error_count > 0 && <span className="text-red-600">✕ {bulkReport.error_count} errors</span>}
            <button onClick={() => setBulkReport(null)} className="ml-auto text-slate-500 hover:text-slate-900"><X className="h-4 w-4 inline"/></button>
          </div>
          {bulkReport.unmatched?.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-slate-600">Unmatched files ({bulkReport.unmatched.length})</summary>
              <ul className="mt-2 pl-4 list-disc text-slate-500 font-mono">
                {bulkReport.unmatched.slice(0,20).map((u,i) => <li key={i}>{u.file}</li>)}
                {bulkReport.unmatched.length > 20 && <li>...and {bulkReport.unmatched.length - 20} more</li>}
              </ul>
            </details>
          )}
          <div className="mt-2 text-[11px] text-slate-500">Tip: name each file after the product (e.g. <code className="font-mono">Noosanta 100mg.jpg</code>). Case & spaces ignored.</div>
        </div>
      )}

      <input placeholder="Search products" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 rounded-lg border border-slate-200 px-3 text-sm min-w-[240px] mb-3"/>

      <input ref={rowFileRef} type="file" accept="image/*" className="hidden" onChange={handleRowFile}/>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left p-3 w-16">Image</th><th className="text-left p-3">Name</th><th className="text-left p-3">Category</th><th className="text-left p-3">$/pill</th><th className="text-left p-3">Rx</th><th className="text-left p-3">Stock</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const perPill = Number(p.sale_price_per_pill || 0);
              const orig = Number(p.original_price_per_pill || 0);
              return (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-3">
                    <button
                      onClick={() => { setRowTarget(p); rowFileRef.current?.click(); }}
                      className="relative w-12 h-12 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 group"
                      title="Upload image"
                      data-testid={`row-upload-${p.id}`}
                    >
                      {p.image_url && <img src={asAbsolute(p.image_url)} alt="" className="w-full h-full object-cover"/>}
                      <div className="absolute inset-0 grid place-items-center bg-slate-900/60 opacity-0 group-hover:opacity-100 transition text-white">
                        {rowUploading === p.id ? <Loader2 className="h-4 w-4 animate-spin"/> : <Upload className="h-4 w-4"/>}
                      </div>
                    </button>
                  </td>
                  <td className="p-3"><div className="font-semibold">{p.name}</div><div className="text-xs text-slate-500">{p.generic_name}{p.strength ? ` · ${p.strength}` : ""}{p.brand ? ` · ${p.brand}` : ""}</div></td>
                  <td className="p-3">{p.category}</td>
                  <td className="p-3 font-mono">
                    {perPill > 0 ? fmtMoney(perPill) : "-"}
                    {orig > perPill && <span className="ml-1 line-through text-xs text-slate-400">{fmtMoney(orig)}</span>}
                  </td>
                  <td className="p-3">{p.prescription_required ? <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Rx</span> : "-"}</td>
                  <td className="p-3">{p.in_stock ? "In stock" : <span className="text-red-600">Out</span>}</td>
                  <td className="p-3 text-right space-x-2">
                    <button onClick={() => open(p)} className="text-slate-700"><Edit className="h-4 w-4 inline"/></button>
                    <button onClick={() => del(p.id)} className="text-red-600" data-testid={`del-${p.id}`}><Trash2 className="h-4 w-4 inline"/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex justify-between items-center">
              <h2 className="font-display font-bold text-lg">{editing?.id ? "Edit product" : "New product"}</h2>
              <button onClick={close} className="p-2"><X className="h-5 w-5"/></button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              {[["name","Product name"],["generic_name","Generic name"],["brand","Brand"],["strength","Strength"],["manufacturer","Manufacturer"],["original_packaging","Original packaging (e.g. 1 x 10)"]].map(([k,l]) => (
                <label key={k} className="block"><span className="text-xs font-semibold">{l}</span>
                  <input value={form[k] || ""} onChange={(e) => setForm({...form, [k]: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid={`pf-${k}`}/>
                </label>
              ))}
              <label className="block"><span className="text-xs font-semibold">Category</span>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="pf-category">
                  <option value="">Choose...</option>
                  {cats.map((c) => <option key={c.id}>{c.name}</option>)}
                </select>
              </label>

              <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
                <div className="text-xs font-semibold mb-2">Product image</div>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                    {form.image_url && <img src={asAbsolute(form.image_url)} alt="" className="w-full h-full object-cover"/>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFormFile}/>
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-dark" data-testid="pf-upload">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Upload className="h-4 w-4"/>}
                      {uploading ? "Uploading..." : "Upload image"}
                    </button>
                    <div className="text-[11px] text-slate-500">Or paste an external URL below (JPG/PNG/WebP, max 6MB).</div>
                    <input value={form.image_url || ""} onChange={(e) => setForm({...form, image_url: e.target.value})} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs font-mono" placeholder="/api/files/blizant/products/... or https://..." data-testid="pf-image_url"/>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 rounded-xl bg-emerald-50/50 border border-emerald-200 p-3">
                <div className="text-xs font-bold uppercase text-emerald-800 mb-2">Per-pill pricing (recommended)</div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <label className="block"><span className="text-xs font-semibold">Sale price / pill ($)</span>
                    <input type="number" step="0.01" value={form.sale_price_per_pill || ""} onChange={(e) => setForm({...form, sale_price_per_pill: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="pf-sale-per-pill"/>
                  </label>
                  <label className="block"><span className="text-xs font-semibold">Original price / pill ($)</span>
                    <input type="number" step="0.01" value={form.original_price_per_pill || ""} onChange={(e) => setForm({...form, original_price_per_pill: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"/>
                  </label>
                  <label className="block"><span className="text-xs font-semibold">Discount %</span>
                    <input type="number" step="0.1" value={form.discount_percentage || ""} onChange={(e) => setForm({...form, discount_percentage: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"/>
                  </label>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">Pack prices auto-calculate from sale-price × pack size (50/100/150/200/300/600).</div>
              </div>

              <label className="block sm:col-span-2"><span className="text-xs font-semibold">Description</span>
                <textarea value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
              </label>

              <details className="sm:col-span-2">
                <summary className="text-xs font-semibold cursor-pointer text-slate-500">Advanced: legacy per-pack overrides</summary>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {PACK_SIZES.map((s) => (
                    <label key={s} className="text-xs">
                      <span className="text-slate-500">{s} pills</span>
                      <input type="number" step="0.01" value={form.price_per_pack?.[s] || ""} onChange={(e) => setPrice(s, e.target.value)} className="mt-1 w-full h-9 rounded border border-slate-200 px-2" data-testid={`pf-price-${s}`}/>
                    </label>
                  ))}
                </div>
              </details>

              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.prescription_required} onChange={(e) => setForm({...form, prescription_required: e.target.checked})} data-testid="pf-rx"/> Prescription required</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.in_stock} onChange={(e) => setForm({...form, in_stock: e.target.checked})}/> In stock</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})}/> Featured on home</label>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={close} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="pf-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
